import { z } from "zod";
import { CUSTOM_COLUMN, MAX_CUSTOM_FIELDS, MAX_CUSTOM_FILTERS, customFilter } from "./custom-fields.js";
/**
 * Saved views: a named, reusable way of looking at tasks.
 *
 * Declared once so the server validates what the web serialises. A view is a
 * lens, not a copy of data — it stores *how* to look (layout, filters, sort,
 * grouping, columns), never which tasks, so it stays right as the work changes.
 */
export const SAVED_VIEW_TYPES = ["list", "board", "table", "calendar", "timeline"];
/** The columns the table view can show. Order here is the default order. */
export const TABLE_COLUMN_IDS = [
    "title",
    "status",
    "priority",
    "owners",
    "due",
    "start",
    "project",
    "section",
    "milestone",
    "tags",
    "checklist",
];
const viewColumnId = z.union([z.enum(TABLE_COLUMN_IDS), z.string().regex(CUSTOM_COLUMN)]);
export const TIMELINE_ZOOMS = ["week", "month", "quarter"];
export const TIMELINE_GROUPS = ["section", "project", "none"];
/** Mirrors the Work filter bar. Every key is optional: absent means "any". */
export const savedViewFilters = z
    .object({
    q: z.string().max(200),
    status: z.string().max(100),
    priority: z.string().max(100),
    mine: z.boolean(),
    overdue: z.boolean(),
    tag: z.string().max(100),
    /** Custom field filters; only meaningful on a project that has the fields. */
    custom: z.array(customFilter).max(MAX_CUSTOM_FILTERS),
})
    .partial()
    .strict();
export const savedViewSort = z
    .object({
    key: z.string().min(1).max(80),
    dir: z.enum(["asc", "desc"]),
})
    .strict();
export const savedViewConfig = z
    .object({
    view: z.enum(SAVED_VIEW_TYPES),
    /** What the board's columns mean (status | due | assignee | priority). */
    grouping: z.string().max(40).optional(),
    /** Table columns: which are shown, and any widths the person dragged. */
    columns: z
        .object({
        visible: z.array(viewColumnId).max(TABLE_COLUMN_IDS.length + MAX_CUSTOM_FIELDS),
        widths: z.record(viewColumnId, z.number().int().min(40).max(800)).optional(),
    })
        .strict()
        .optional(),
    zoom: z.enum(TIMELINE_ZOOMS).optional(),
    timelineGroup: z.enum(TIMELINE_GROUPS).optional(),
})
    .strict();
export const createSavedViewBody = z
    .object({
    name: z.string().trim().min(1).max(100),
    /** Personal views belong to their owner; shared ones belong to a project. */
    shared: z.boolean().default(false),
    /** Required when shared; for a personal view it limits it to that project's screen. */
    projectId: z.string().uuid().nullable().optional(),
    config: savedViewConfig,
    filters: savedViewFilters.default({}),
    sort: savedViewSort.nullable().default(null),
})
    .strict();
export const patchSavedViewBody = z
    .object({
    name: z.string().trim().min(1).max(100).optional(),
    config: savedViewConfig.optional(),
    filters: savedViewFilters.optional(),
    sort: savedViewSort.nullable().optional(),
})
    .strict();
//# sourceMappingURL=saved-views.js.map